import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { KeyRound, Download, Inbox, RefreshCw, Sparkles, X, Copy, Loader2 } from "lucide-react";
import PageHero from "../components/PageHero";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [key, setKey] = useState(sessionStorage.getItem("cn_admin_key") || "");
  const [enquiries, setEnquiries] = useState(null);
  const [loading, setLoading] = useState(false);
  const [drafting, setDrafting] = useState(null);
  const [draftModal, setDraftModal] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/enquiries`, { headers: { "X-Admin-Key": key } });
      setEnquiries(res.data.enquiries);
      sessionStorage.setItem("cn_admin_key", key);
    } catch {
      toast.error("Access denied", { description: "The admin key is incorrect." });
      setEnquiries(null);
    } finally {
      setLoading(false);
    }
  };

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/enquiries/export`, {
        headers: { "X-Admin-Key": key },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "crednova-enquiries.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed", { description: "Please try again." });
    }
  };

  const openDraft = async (e, regenerate = false) => {
    setDraftModal({ id: e.id, name: e.full_name, summary: e.ai_summary || "", draft: e.ai_draft || "" });
    if (e.ai_draft && !regenerate) return;
    setDrafting(e.id);
    try {
      const res = await axios.post(`${API}/enquiries/${e.id}/draft`, {}, { headers: { "X-Admin-Key": key } });
      setDraftModal({ id: e.id, name: e.full_name, summary: res.data.summary, draft: res.data.draft });
      setEnquiries((prev) =>
        prev.map((x) => (x.id === e.id ? { ...x, ai_summary: res.data.summary, ai_draft: res.data.draft } : x))
      );
    } catch {
      toast.error("AI draft failed", { description: "Please try again in a moment." });
      setDraftModal(null);
    } finally {
      setDrafting(null);
    }
  };

  const copyDraft = () => {
    navigator.clipboard
      .writeText(draftModal.draft)
      .then(() => toast.success("Draft copied"))
      .catch(() => toast.error("Copy failed"));
  };

  return (
    <main>
      <PageHero
        testid="admin-hero"
        eyebrow="Internal"
        titleLines={["Enquiry Dashboard"]}
        description="View and export loan assistance enquiries submitted through the website."
      />

      <section className="px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto pb-20 lg:pb-28" data-testid="admin-section">
        {enquiries === null && (
          <div className="card-gold max-w-md mx-auto p-8 hover:!transform-none">
            <label htmlFor="admin-key" className="block text-xs text-[#A1A1AA] mb-2 tracking-wide">Admin Key</label>
            <input
              id="admin-key"
              data-testid="admin-key-input"
              type="password"
              className="input-gold mb-5"
              placeholder="Enter admin key"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
            />
            <button
              data-testid="admin-view-button"
              onClick={load}
              disabled={loading || !key}
              className="btn-gold w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm disabled:opacity-60"
            >
              <span className="relative z-10 flex items-center gap-2">
                <KeyRound size={15} /> {loading ? "Loading..." : "View Enquiries"}
              </span>
            </button>
          </div>
        )}

        {enquiries !== null && (
          <div>
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
              <p className="text-sm text-[#A1A1AA]" data-testid="admin-enquiry-count">
                <span className="gold-text font-semibold">{enquiries.length}</span> enquiries received
              </p>
              <div className="flex gap-3">
                <button
                  data-testid="admin-refresh-button"
                  onClick={load}
                  className="btn-outline-gold inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-xs"
                >
                  <RefreshCw size={13} /> Refresh
                </button>
                <button
                  data-testid="admin-export-button"
                  onClick={exportCsv}
                  className="btn-gold inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-xs"
                >
                  <span className="relative z-10 flex items-center gap-2">
                    <Download size={13} /> Export CSV
                  </span>
                </button>
              </div>
            </div>

            {enquiries.length === 0 ? (
              <div className="card-gold p-14 text-center hover:!transform-none" data-testid="admin-empty-state">
                <Inbox size={28} className="text-[#D4AF37] mx-auto mb-4" strokeWidth={1.5} />
                <p className="text-sm text-[#A1A1AA]">No enquiries yet. New submissions will appear here.</p>
              </div>
            ) : (
              <div className="card-gold overflow-x-auto hover:!transform-none" data-testid="admin-enquiry-table">
                <table className="w-full text-sm min-w-[860px]">
                  <thead>
                    <tr className="border-b border-[rgba(212,175,55,0.2)] text-left">
                      {["Date", "Name", "Phone", "Email", "Category", "Amount", "Message", "AI"].map((h) => (
                        <th key={h} className="px-5 py-4 font-mono-brand text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] font-medium">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {enquiries.map((e) => (
                      <tr key={e.id} className="border-b border-[rgba(212,175,55,0.08)] hover:bg-[#121216] transition-colors" data-testid={`admin-enquiry-row-${e.id}`}>
                        <td className="px-5 py-4 text-xs text-[#71717A] whitespace-nowrap">
                          {new Date(e.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                        </td>
                        <td className="px-5 py-4 text-[#F4F4F6]">{e.full_name}</td>
                        <td className="px-5 py-4 text-[#A1A1AA]">{e.phone}</td>
                        <td className="px-5 py-4 text-[#A1A1AA]">{e.email}</td>
                        <td className="px-5 py-4 text-[#A1A1AA]">{e.loan_category}</td>
                        <td className="px-5 py-4 text-[#A1A1AA]">{e.desired_amount || "—"}</td>
                        <td className="px-5 py-4 text-[#A1A1AA] max-w-[220px] truncate">{e.message || "—"}</td>
                        <td className="px-5 py-4">
                          <button
                            data-testid={`admin-draft-button-${e.id}`}
                            onClick={() => openDraft(e)}
                            className="btn-outline-gold inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[11px] whitespace-nowrap"
                          >
                            <Sparkles size={12} /> {e.ai_draft ? "View Draft" : "AI Draft"}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </section>

      {draftModal && (
        <div
          className="fixed inset-0 z-[80] bg-black/75 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="admin-draft-modal"
          onClick={(e) => e.target === e.currentTarget && setDraftModal(null)}
        >
          <div className="card-gold hover:!transform-none w-full max-w-lg p-7 max-h-[85vh] overflow-y-auto">
            <div className="flex items-start justify-between gap-4 mb-5">
              <div>
                <p className="caption-gold mb-1.5">AI Copilot</p>
                <h3 className="font-display text-lg font-semibold text-white">Reply Draft — {draftModal.name}</h3>
              </div>
              <button
                data-testid="admin-draft-close-button"
                onClick={() => setDraftModal(null)}
                aria-label="Close draft"
                className="text-[#71717A] hover:text-white p-1 transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            {drafting === draftModal.id ? (
              <div className="flex items-center gap-3 text-sm text-[#A1A1AA] py-10 justify-center" data-testid="admin-draft-loading">
                <Loader2 size={16} className="animate-spin text-[#D4AF37]" /> Claude is drafting...
              </div>
            ) : (
              <>
                {draftModal.summary && (
                  <div className="mb-5">
                    <p className="text-[10px] font-mono-brand uppercase tracking-[0.2em] text-[#D4AF37] mb-2">Summary</p>
                    <p className="text-sm text-[#A1A1AA] leading-relaxed" data-testid="admin-draft-summary">{draftModal.summary}</p>
                  </div>
                )}
                <div className="mb-6">
                  <p className="text-[10px] font-mono-brand uppercase tracking-[0.2em] text-[#D4AF37] mb-2">Suggested Reply</p>
                  <div className="rounded-xl border border-[rgba(212,175,55,0.2)] bg-[#121216] p-4">
                    <p className="text-sm text-[#E4E4E7] leading-relaxed whitespace-pre-wrap" data-testid="admin-draft-text">{draftModal.draft}</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <button
                    data-testid="admin-draft-copy-button"
                    onClick={copyDraft}
                    className="btn-gold flex-1 inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-xs"
                  >
                    <span className="relative z-10 flex items-center gap-2">
                      <Copy size={13} /> Copy Reply
                    </span>
                  </button>
                  <button
                    data-testid="admin-draft-regenerate-button"
                    onClick={() => openDraft(enquiries.find((x) => x.id === draftModal.id), true)}
                    className="btn-outline-gold inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-xs"
                  >
                    <RefreshCw size={13} /> Regenerate
                  </button>
                </div>
                <p className="text-[10px] text-[#52525B] mt-4 leading-snug">
                  AI-generated draft — review before sending. Never promises loan approval; edit as needed.
                </p>
              </>
            )}
          </div>
        </div>
      )}
    </main>
  );
}
